import { fmtFecha } from '../../lib/utils';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { comprasApi } from '../../lib/api';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RefreshCw, FileText, Search, Eye, CheckCircle, Clock, Printer, Database, Globe, ShieldCheck, Download, Mail, Send, Loader2, FileSearch, Pencil, Unlock, AlertTriangle, XCircle, RotateCcw, Zap, FileSpreadsheet, Package, Wallet, DollarSign, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { TableSkeleton } from '../../lib/skeletons';
import { useAuth } from '../../lib/auth';
import { OCNumber } from '../../lib/ocFormat';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { useDocRef } from '../../contexts/DocRefContext';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { exportToCSV, printTable } from '../../lib/printUtils';

export const OrdenesCompraList = () => {
  const { user } = useAuth();
  const { openDoc } = useDocRef();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ ordenes_compra: [], total: 0 });
  const [error, setError] = useState(null);
  const [filtroOT, setFiltroOT] = useState('');
  const [filtroOC, setFiltroOC] = useState('');
  const [soloAprobadas, setSoloAprobadas] = useState(false);
  const [soloSinVb, setSoloSinVb] = useState(false);
  const [verAnuladas, setVerAnuladas] = useState(false);
  const [filtroOrigen, setFiltroOrigen] = useState(''); // '' | 'web' | 'access'
  const [filtroDoc, setFiltroDoc] = useState(''); // '' | 'sin' | 'con'
  // Iter 338 (Renzo): filtro OCs pendientes de recepción + recepción exprés
  const [soloSinRecepcion, setSoloSinRecepcion] = useState(false);
  const [recepExpressOC, setRecepExpressOC] = useState(null);
  const [recepExpressForm, setRecepExpressForm] = useState({ tipo: 'GD', numDoc: '', destino: 'bodega' });
  const [recepExpressLoading, setRecepExpressLoading] = useState(false);
  const [selectedOC, setSelectedOC] = useState(null);
  const [detalleOC, setDetalleOC] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [detallePdfUrl, setDetallePdfUrl] = useState(null);
  const [detallePdfLoading, setDetallePdfLoading] = useState(false);
  // Iter 313.h — Historial de TC
  const [tcHistorialOC, setTcHistorialOC] = useState(null);
  const [tcHistorialData, setTcHistorialData] = useState(null);
  const [tcHistorialLoading, setTcHistorialLoading] = useState(false);
  // Paginación: 100 OCs por página por default (alineado con backend default).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Iter 236.y (Renzo, feb-2026) — Egreso directo en Alegra desde OC (Contado/TC/Cheque)
  const [egresoDialog, setEgresoDialog] = useState({ open: false, oc: null });
  const [egresoPreview, setEgresoPreview] = useState(null);
  const [egresoLoading, setEgresoLoading] = useState(false);
  const [egresoSaving, setEgresoSaving] = useState(false);

  // Leer URL params al montar (e.g., /compras/ordenes-compra?estado=sin_vb&source=web)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const estado = params.get('estado');
    const src = params.get('source');
    if (estado === 'sin_vb') setSoloSinVb(true);
    if (estado === 'aprobadas') setSoloAprobadas(true);
    if (src === 'web' || src === 'access') setFiltroOrigen(src);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter 313.h — Cargar historial TC cuando se abre el modal
  useEffect(() => {
    if (!tcHistorialOC) {
      setTcHistorialData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setTcHistorialLoading(true);
      try {
        const r = await api.get(`/compras/ordenes-compra/${tcHistorialOC}/tc-historial`);
        if (!cancelled) setTcHistorialData(r.data);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Error al cargar historial TC');
      } finally {
        if (!cancelled) setTcHistorialLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tcHistorialOC]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soloAprobadas, soloSinVb, filtroOrigen, verAnuladas, soloSinRecepcion, page, pageSize]);

  // Reset a page 1 cuando cambian filtros (no cuando cambia la página misma)
  useEffect(() => {
    setPage(1);
   
  }, [soloAprobadas, soloSinVb, filtroOrigen, verAnuladas, soloSinRecepcion]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        solo_aprobadas: soloAprobadas,
        solo_sin_vb: soloSinVb,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (filtroOrigen) params.source = filtroOrigen;
      if (verAnuladas) params.solo_anuladas = true;
      if (soloSinRecepcion) params.recepcion_pendiente = true;
      if (filtroOT) params.ot_numero = parseInt(filtroOT);
      if (filtroOC && filtroOC.trim()) {
        params.q = filtroOC.trim();
        // Búsqueda libre requiere mirar histórico completo
        params.fecha_desde = 'all';
        params.fecha_hasta = 'all';
      }
      const res = await comprasApi.getOrdenesCompra(params);
      setData(res.data);
    } catch (err) {
      setError('Error al cargar las órdenes de compra');

    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => loadData();

  // ────────────────── Cmd+K: comandos contextuales ──────────────────
  useRegisterCmdKCommands(
    'oc-list',
    'Órdenes de Compra',
    [
      {
        id: 'oc-filter-sin-vb',
        label: 'Filtrar: solo OCs sin VB',
        sublabel: 'Pendientes de firma',
        icon: 'Filter',
        keywords: 'filtro vb pendientes firma',
        run: () => { setSoloSinVb(true); setSoloAprobadas(false); setVerAnuladas(false); },
        enabled: !soloSinVb,
      },
      {
        id: 'oc-filter-aprobadas',
        label: 'Filtrar: solo OCs aprobadas',
        sublabel: 'Con VB',
        icon: 'Filter',
        keywords: 'filtro aprobadas vb firmadas',
        run: () => { setSoloAprobadas(true); setSoloSinVb(false); setVerAnuladas(false); },
        enabled: !soloAprobadas,
      },
      {
        id: 'oc-filter-sin-doc',
        label: 'Filtrar: solo OCs sin Doc. Recibido',
        sublabel: 'Sin factura/guía cargada',
        icon: 'Filter',
        keywords: 'filtro sin documento recibido factura guia',
        run: () => setFiltroDoc('sin'),
        enabled: filtroDoc !== 'sin',
      },
      {
        id: 'oc-filter-con-doc',
        label: 'Filtrar: solo OCs con Doc. Recibido',
        icon: 'Filter',
        keywords: 'filtro con documento recibido',
        run: () => setFiltroDoc('con'),
        enabled: filtroDoc !== 'con',
      },
      {
        id: 'oc-clear-filters',
        label: 'Limpiar todos los filtros',
        icon: 'RefreshCw',
        keywords: 'limpiar filtros reset',
        run: () => {
          setSoloAprobadas(false); setSoloSinVb(false); setVerAnuladas(false);
          setFiltroOrigen(''); setFiltroDoc(''); setFiltroOT(''); setFiltroOC('');
        },
        enabled: soloAprobadas || soloSinVb || verAnuladas || !!filtroOrigen || !!filtroDoc || !!filtroOT || !!filtroOC,
      },
      {
        id: 'oc-refresh',
        label: 'Refrescar listado',
        icon: 'RefreshCw',
        keywords: 'refrescar recargar actualizar',
        run: () => loadData(),
      },
    ],
    [soloSinVb, soloAprobadas, verAnuladas, filtroOrigen, filtroDoc, filtroOT, filtroOC],
  );

  const handleVerDetalle = async (numeroOc) => {
    setLoadingDetalle(true);
    setSelectedOC(numeroOc);
    setDetallePdfUrl(null);
    setDetallePdfLoading(true);
    try {
      const res = await comprasApi.getOrdenCompraDetalle(numeroOc);
      setDetalleOC(res.data);
    } catch (err) {

      setDetalleOC(null);
    } finally {
      setLoadingDetalle(false);
    }
    // Cargar PDF en paralelo al detalle
    try {
      const res = await api.get(`/compras/ordenes-compra/${numeroOc}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setDetallePdfUrl(url);
    } catch { /* sin preview */ } finally {
      setDetallePdfLoading(false);
    }
  };

  // Auto-open OC detail if navigated with ?open=NUMBER (e.g., desde widget alerta de Dashboard)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const numeroOc = params.get('open');
    if (numeroOc && !selectedOC) {
      handleVerDetalle(numeroOc);
      // Limpiar el parámetro para que refrescar no vuelva a abrirlo
      navigate(location.pathname, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const closeDetalle = () => {
    setSelectedOC(null);
    setDetalleOC(null);
    if (detallePdfUrl) { window.URL.revokeObjectURL(detallePdfUrl); }
    setDetallePdfUrl(null);
    setDetallePdfLoading(false);
  };

  const canVB = user && (user.rol === 'admin' || user.rol === 'gerencia' || user.permisos?.compras?.vb);
  const canEdit = user && (
    user.rol === 'admin' ||
    user.rol === 'gerencia' ||
    user.permisos?.compras?.editar ||
    user.permisos?.compras?.generar_oc
  );
  // Solo Gerencia/Admin puede desbloquear OCs ya aprobadas
  const canDesbloquear = user && (user.rol === 'admin' || user.rol === 'gerencia');
  // Solo Gerencia/Admin puede anular o desanular OCs
  const canAnular = user && (user.rol === 'admin' || user.rol === 'gerencia');

  // Anular OC dialog
  const [anularOpen, setAnularOpen] = useState(false);
  const [anularMotivo, setAnularMotivo] = useState('');
  const [anularLoading, setAnularLoading] = useState(false);

  // Iter 236.m.16 (Carlos): permisos para toggle requiere_recepcion + cierre manual
  const canToggleRecepcion = user && (
    user.rol === 'admin' || user.rol === 'administrador' || user.rol === 'gerencia' ||
    user.rol === 'compras' || user.rol === 'comprador' || user.rol === 'jefe_compras' ||
    user.permisos?.compras?.generar_oc ||
    user.permisos?.compras?.editar
  );
  const [togglingOC, setTogglingOC] = useState(null);

  const toggleRequiereRecepcion = async (oc) => {
    const num = oc.numero_oc;
    const next = !oc.requiere_recepcion;
    setTogglingOC(num);
    try {
      await api.patch(`/compras/ordenes-compra/${num}/requiere-recepcion`, {
        requiere_recepcion: next,
      });
      setData(prev => ({
        ...prev,
        ordenes_compra: prev.ordenes_compra.map(o =>
          o.numero_oc === num
            ? { ...o, requiere_recepcion: next, recepcion_cerrada: !next ? true : o.recepcion_cerrada }
            : o
        ),
      }));
      toast.success(
        next
          ? `OC ${num} marcada para recepción de bodega`
          : `OC ${num} ya no requiere recepción`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo actualizar el flag');
    } finally {
      setTogglingOC(null);
    }
  };

  const handleRecepcionExpress = async () => {
    const oc = recepExpressOC;
    if (!oc || !recepExpressForm.numDoc.trim()) return;
    setRecepExpressLoading(true);
    try {
      const res = await api.post('/bodega/recepcionar-todo', {
        numero_oc: String(oc.numero_oc),
        fecha: new Date().toISOString().slice(0, 10),
        tipo_documento: recepExpressForm.tipo,
        numero_documento: recepExpressForm.numDoc.trim(),
        responsable: user?.nombre || user?.email || 'Compras',
        guia_despacho: recepExpressForm.tipo === 'GD' ? recepExpressForm.numDoc.trim() : '',
        destino: recepExpressForm.destino,
        items: [],
      });
      toast.success(`Recepción ${res.data?.numero_recepcion || ''} creada — OC ${oc.numero_oc} recepcionada completa`);
      setData(prev => ({
        ...prev,
        ordenes_compra: prev.ordenes_compra.map(o =>
          o.numero_oc === oc.numero_oc ? { ...o, tiene_recepcion: true } : o
        ),
      }));
      setRecepExpressOC(null);
      if (soloSinRecepcion) setTimeout(loadData, 300);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo crear la recepción exprés');
    } finally {
      setRecepExpressLoading(false);
    }
  };

  const openAnularDialog = () => {
    setAnularMotivo('');
    setAnularOpen(true);
  };

  const handleAnular = async () => {
    if (!anularMotivo || anularMotivo.trim().length < 5) {
      toast.error('Ingrese un motivo de al menos 5 caracteres');
      return;
    }
    setAnularLoading(true);
    try {
      const res = await api.post(`/compras/ordenes-compra/${selectedOC}/anular`, {
        motivo: anularMotivo.trim(),
      });
      const msg = res.data.tenia_contabilizadas > 0
        ? `OC ${selectedOC} anulada (tenía ${res.data.tenia_contabilizadas} factura(s) contabilizada(s) — revisá Contabilidad)`
        : `OC ${selectedOC} anulada exitosamente`;
      toast.success(msg);
      setAnularOpen(false);
      if (detalleOC) {
        setDetalleOC(prev => prev ? {
          ...prev,
          orden_compra: {
            ...prev.orden_compra,
            anulada: true,
            anulada_motivo: anularMotivo.trim(),
            anulada_at: res.data.anulada_at,
          }
        } : prev);
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al anular OC');
    } finally {
      setAnularLoading(false);
    }
  };

  const handleDesanular = async (numeroOc) => {
    if (!window.confirm(`¿Desanular la OC ${numeroOc}?\n\nVolverá a aparecer en dashboards y listados normales.`)) return;
    try {
      await api.post(`/compras/ordenes-compra/${numeroOc}/desanular`);
      toast.success(`OC ${numeroOc} desanulada`);
      if (detalleOC) {
        setDetalleOC(prev => prev ? {
          ...prev,
          orden_compra: { ...prev.orden_compra, anulada: false, anulada_motivo: '', anulada_at: '' }
        } : prev);
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desanular OC');
    }
  };

  // Desbloquear OC dialog
  const [desbloquearOpen, setDesbloquearOpen] = useState(false);
  const [desbloquearMotivo, setDesbloquearMotivo] = useState('');
  const [desbloquearLoading, setDesbloquearLoading] = useState(false);

  const openDesbloquearDialog = () => {
    setDesbloquearMotivo('');
    setDesbloquearOpen(true);
  };

  const handleDesbloquear = async () => {
    if (!desbloquearMotivo || desbloquearMotivo.trim().length < 3) {
      toast.error('Ingrese un motivo de al menos 3 caracteres');
      return;
    }
    setDesbloquearLoading(true);
    try {
      await api.post(`/compras/ordenes-compra/${selectedOC}/desbloquear`, {
        motivo: desbloquearMotivo.trim(),
      });
      toast.success(`OC ${selectedOC} desbloqueada. El comprador puede editarla.`);
      setDesbloquearOpen(false);
      // Refrescar el detalle para reflejar cambio de estado
      if (detalleOC) {
        setDetalleOC(prev => prev ? {
          ...prev,
          orden_compra: {
            ...prev.orden_compra,
            aprobada: false,
            estado: 'pendiente_aprobacion',
            vb_gerencia: false,
            desbloqueada: true,
            desbloqueada_motivo: desbloquearMotivo.trim(),
          }
        } : prev);
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desbloquear OC');
    } finally {
      setDesbloquearLoading(false);
    }
  };

  const handleAprobar = async (numeroOc) => {
    try {
      await api.put(`/compras/ordenes-compra/${numeroOc}/aprobar`);
      toast.success(`OC ${numeroOc} aprobada exitosamente`);
      loadData();
      if (detalleOC) {
        setDetalleOC(prev => prev ? { ...prev, orden_compra: { ...prev.orden_compra, aprobada: true, estado: 'aprobada' } } : prev);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aprobar OC');
    }
  };

  // Iter 236.y (Renzo, feb-2026) — Repetir OC: navega a /compras/generar-oc?repetir_de=NNN
  const handleRepetirOC = (numeroOc) => {
    navigate(`/compras/generar-oc?repetir_de=${encodeURIComponent(numeroOc)}`);
  };

  // Iter 236.y (Renzo, feb-2026) — Registrar Egreso Alegra (Contado/TC/Cheque)
  const openEgresoAlegra = async (oc) => {
    setEgresoDialog({ open: true, oc });
    setEgresoPreview(null);
    setEgresoLoading(true);
    try {
      const r = await api.get(`/compras/ordenes-compra/${oc.numero_oc}/egreso-alegra/preview`);
      setEgresoPreview(r.data);
    } catch (err) {
      toast.error('No se puede generar egreso', { description: err?.response?.data?.detail });
      setEgresoDialog({ open: false, oc: null });
    } finally {
      setEgresoLoading(false);
    }
  };

  const confirmEgresoAlegra = async () => {
    const oc = egresoDialog.oc;
    if (!oc?.numero_oc) return;
    setEgresoSaving(true);
    try {
      const r = await api.post(`/compras/ordenes-compra/${oc.numero_oc}/egreso-alegra`);
      toast.success(r.data.message);
      setEgresoDialog({ open: false, oc: null });
      setEgresoPreview(null);
      loadData();
    } catch (err) {
      toast.error('Alegra rechazó', { description: err?.response?.data?.detail });
    } finally {
      setEgresoSaving(false);
    }
  };

  const handleDescargarPDF = async (numeroOc) => {
    try {
      const res = await api.get(`/compras/ordenes-compra/${numeroOc}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Error generando PDF');
    }
  };

  // Email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailOC, setEmailOC] = useState('');
  const [emailDest, setEmailDest] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMaestro, setEmailMaestro] = useState('');
  const [actualizarMaestro, setActualizarMaestro] = useState(false);
  const [emailHistorial, setEmailHistorial] = useState([]);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const openEmailDialog = async (numeroOc) => {
    setEmailOC(numeroOc);
    setEmailDest('');
    setEmailMaestro('');
    setActualizarMaestro(false);
    setEmailMsg('');
    setEmailHistorial([]);
    setPdfPreviewUrl(null);
    setPdfLoading(true);
    setEmailOpen(true);
    // Prellenar el correo de COMPRAS del proveedor desde el maestro (antes del PDF, que es lento)
    try {
      const res = await comprasApi.getOrdenCompraDetalle(numeroOc);
      const prov = res.data?.proveedor || {};
      if (prov.email) { setEmailDest(prov.email); setEmailMaestro(prov.email); }
    } catch { }
    // Historial de envíos previos
    try {
      const h = await api.get(`/compras/ordenes-compra/${numeroOc}/emails`);
      setEmailHistorial(h.data?.envios || []);
    } catch { }
    // Load PDF preview
    try {
      const res = await api.get(`/compras/ordenes-compra/${numeroOc}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfPreviewUrl(url);
    } catch { }
    setPdfLoading(false);
  };

  // Cleanup PDF blob URL when dialog closes
  const closeEmailDialog = (open) => {
    if (!open && pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    setEmailOpen(open);
  };

  const handleEnviarEmail = async () => {
    if (!emailDest) { toast.error('Ingrese email destino'); return; }
    setEmailSending(true);
    try {
      const res = await api.post(`/compras/ordenes-compra/${emailOC}/enviar-email`, {
        email_destino: emailDest,
        mensaje_adicional: emailMsg,
        actualizar_maestro: actualizarMaestro,
      });
      toast.success(res.data.message);
      if (res.data.maestro_actualizado) toast.success('Correo de compras actualizado en el maestro del proveedor');
      setEmailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar correo');
    } finally {
      setEmailSending(false);
    }
  };

  const fmt = (num) => num?.toLocaleString('es-CL') || '0';
  const handlePrint = () => {
    // Carlos (09/05/2026): imprime las OCs visibles (página actual + filtros aplicados)
    // con header CHIRINO STEEL. Si quiere imprimir TODO el histórico, puede subir
    // pageSize o usar el botón "Excel" que también respeta lo cargado.
    const ocs = ordenesFiltradas;
    if (!ocs.length) {
      toast.warning('No hay OCs para imprimir con los filtros actuales');
      return;
    }
    printTable({
      title: 'Listado de Órdenes de Compra',
      subtitle: `${ocs.length} OCs · Página ${page} · Generado ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'OCs visibles', value: ocs.length },
        { label: 'Total general', value: data.total ?? 0 },
        { label: 'Filtro origen', value: filtroOrigen ? filtroOrigen.toUpperCase() : 'Todas' },
        { label: 'Filtro doc', value: filtroDoc === 'sin' ? 'Sin doc.' : filtroDoc === 'con' ? 'Con doc.' : 'Todas' },
      ],
      columns: [
        { key: 'numero_oc', label: 'OC', width: '60px', mono: true, align: 'center' },
        { key: 'fecha_creacion', label: 'Fecha', format: (v) => fmtFecha(v) || '-' },
        { key: 'razon_social_proveedor', label: 'Proveedor', format: (v, r) => v || r.rut_proveedor || '-' },
        { key: 'numero_documento', label: 'Doc.', format: (v) => v || '' },
        { key: 'tipo_documento', label: 'Tipo doc.' },
        { key: 'ot_numero', label: 'OT', align: 'center' },
        { key: 'creado_por', label: 'Creado por' },
        { key: 'estado_vb', label: 'Estado VB', align: 'center', format: (v) => (v || 'pendiente').toUpperCase() },
        { key: 'subtotal_neto', label: 'Total neto', align: 'right', mono: true, format: (v) => `$${(Math.round(Number(v) || 0)).toLocaleString('es-CL')}` },
      ],
      rows: ocs,
    });
  };

  const handleExcel = () => {
    const ocs = ordenesFiltradas;
    if (!ocs.length) {
      toast.warning('No hay OCs para exportar con los filtros actuales');
      return;
    }
    exportToCSV({
      filename: `ordenes_compra_pag${page}`,
      title: 'CHIRINO STEEL · Listado de Órdenes de Compra',
      summary: [
        { label: 'OCs en archivo', value: ocs.length },
        { label: 'Total general en sistema', value: data.total ?? 0 },
        { label: 'Página actual', value: page },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'numero_oc', label: 'N. OC' },
        { key: 'fecha_creacion', label: 'Fecha creación', format: (v) => fmtFecha(v) || '' },
        { key: 'rut_proveedor', label: 'RUT proveedor' },
        { key: 'razon_social_proveedor', label: 'Proveedor' },
        { key: 'numero_documento', label: 'N. Documento' },
        { key: 'tipo_documento', label: 'Tipo Doc' },
        { key: 'ot_numero', label: 'OT' },
        { key: 'creado_por', label: 'Creado por' },
        { key: 'estado_vb', label: 'Estado VB', format: (v) => (v || 'pendiente').toUpperCase() },
        { key: 'forma_pago', label: 'Forma Pago' },
        { key: 'plazo_pago', label: 'Plazo' },
        { key: 'subtotal_neto', label: 'Total Neto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'subtotal_iva', label: 'IVA (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'subtotal_total', label: 'Total Bruto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'source', label: 'Origen' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: ocs,
    });
  };

  if (loading) {
    return <TableSkeleton rows={8} cols={7} />;
  }

  // Filtro client-side por presencia/ausencia de documento físico recibido.
  // Carlos (2026-04-27): Sin Doc / Con Doc / Todas.
  const ordenesFiltradas = (() => {
    if (!data?.ordenes_compra) return [];
    if (!filtroDoc) return data.ordenes_compra;
    return data.ordenes_compra.filter(oc => {
      const tiene = !!(oc.numero_documento && String(oc.numero_documento).trim());
      return filtroDoc === 'sin' ? !tiene : tiene;
    });
  })();

  return (
    <div className="p-4" data-testid="ordenes-compra-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 print:hidden">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Órdenes de Compra
          </h1>
          <p className="text-sm text-muted-foreground">
            {data.total} órdenes de compra
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" size="sm" data-testid="btn-refresh-ocs">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handlePrint} variant="outline" size="sm" title="Imprimir listado" data-testid="btn-print-ocs">
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button onClick={handleExcel} variant="outline" size="sm" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel/CSV" data-testid="btn-excel-ocs">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 print:hidden items-center">
        <Input
          placeholder="N° OC o proveedor"
          value={filtroOC}
          onChange={(e) => setFiltroOC(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          className="w-52"
          data-testid="filtro-oc-input"
        />
        <Input
          placeholder="N° OT"
          value={filtroOT}
          onChange={(e) => setFiltroOT(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          className="w-28"
          data-testid="filtro-ot-input"
        />
        <Button onClick={handleSearch} size="sm" data-testid="buscar-btn">
          <Search className="w-4 h-4 mr-1" /> Buscar
        </Button>
        {(filtroOC || filtroOT || soloAprobadas || soloSinVb || filtroOrigen || verAnuladas || filtroDoc || soloSinRecepcion) && (
          <Button
            onClick={() => { setFiltroOC(''); setFiltroOT(''); setSoloAprobadas(false); setSoloSinVb(false); setFiltroOrigen(''); setVerAnuladas(false); setFiltroDoc(''); setSoloSinRecepcion(false); setTimeout(loadData, 0); }}
            variant="ghost"
            size="sm"
            data-testid="limpiar-filtros-btn"
          >
            Limpiar
          </Button>
        )}
        {/* Toggle Origen Web/Access/Todas */}
        <div className="flex items-center gap-1 text-xs border rounded-md overflow-hidden h-8 ml-2" data-testid="oc-list-origen">
          <span className="px-2 text-gray-500 bg-gray-50 h-full flex items-center">Origen:</span>
          <button
            type="button"
            onClick={() => setFiltroOrigen('')}
            data-testid="oc-list-origen-todas"
            className={`px-2.5 h-full transition-colors ${filtroOrigen === '' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            Todas
          </button>
          <button
            type="button"
            onClick={() => setFiltroOrigen('web')}
            data-testid="oc-list-origen-web"
            className={`px-2.5 h-full transition-colors ${filtroOrigen === 'web' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            Web
          </button>
          <button
            type="button"
            onClick={() => setFiltroOrigen('access')}
            data-testid="oc-list-origen-access"
            className={`px-2.5 h-full transition-colors ${filtroOrigen === 'access' ? 'bg-slate-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            Access
          </button>
        </div>
        {/* Chip estado: Sin VB */}
        <button
          type="button"
          onClick={() => { setSoloSinVb(!soloSinVb); if (!soloSinVb) setSoloAprobadas(false); }}
          data-testid="oc-list-chip-sin-vb"
          className={`text-xs px-3 h-8 rounded-md border transition-colors ${soloSinVb ? 'bg-amber-500 text-white border-amber-600' : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50'}`}
        >
          {soloSinVb ? '✓ ' : ''}Sin VB
        </button>
        {/* Chip Ver Anuladas */}
        <button
          type="button"
          onClick={() => setVerAnuladas(!verAnuladas)}
          data-testid="oc-list-chip-anuladas"
          className={`text-xs px-3 h-8 rounded-md border transition-colors ${verAnuladas ? 'bg-red-600 text-white border-red-700' : 'bg-white text-red-700 border-red-200 hover:bg-red-50'}`}
          title="Ver solo OCs anuladas"
        >
          {verAnuladas ? '✓ ' : ''}Anuladas
        </button>
        {/* Toggle Doc Recibido (Carlos 2026-04-27) */}
        <div className="flex items-center gap-1 text-xs border rounded-md overflow-hidden h-8" data-testid="oc-list-doc">
          <span className="px-2 text-gray-500 bg-gray-50 h-full flex items-center">Doc:</span>
          <button type="button" onClick={() => setFiltroDoc('')} data-testid="oc-list-doc-todas"
            className={`px-2.5 h-full transition-colors ${filtroDoc === '' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            Todas
          </button>
          <button type="button" onClick={() => setFiltroDoc('sin')} data-testid="oc-list-doc-sin"
            className={`px-2.5 h-full transition-colors ${filtroDoc === 'sin' ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 hover:bg-amber-50'}`}
            title="Solo OCs SIN documento físico recibido (factura/guía/boleta)">
            Sin Doc
          </button>
          <button type="button" onClick={() => setFiltroDoc('con')} data-testid="oc-list-doc-con"
            className={`px-2.5 h-full transition-colors ${filtroDoc === 'con' ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 hover:bg-emerald-50'}`}
            title="Solo OCs CON documento físico recibido">
            Con Doc
          </button>
        </div>
        {/* Iter 338 (Renzo): OCs pendientes de recepción de materiales */}
        <button
          type="button"
          onClick={() => setSoloSinRecepcion(!soloSinRecepcion)}
          data-testid="oc-list-chip-sin-recepcion"
          className={`text-xs px-3 h-8 rounded-md border transition-colors ${soloSinRecepcion ? 'bg-teal-600 text-white border-teal-700' : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50'}`}
          title="Solo OCs que requieren recepción de bodega y aún NO tienen recepción registrada"
        >
          {soloSinRecepcion ? '✓ ' : ''}Sin Recepción
        </button>
        <label className="flex items-center gap-2 text-sm ml-1">
          <input
            type="checkbox"
            checked={soloAprobadas}
            onChange={(e) => { setSoloAprobadas(e.target.checked); if (e.target.checked) setSoloSinVb(false); }}
            data-testid="oc-list-solo-aprobadas"
          />
          Solo aprobadas
        </label>
      </div>

      {error && <p className="text-destructive mb-4">{error}</p>}

      {/* Modal Detalle */}
      {selectedOC && createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4 print:hidden">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-3 border-b bg-gray-100 flex-shrink-0">
              <h3 className="font-bold text-sm">
                {(() => {
                  const oc = detalleOC?.orden_compra;
                  const nombre = oc?.proveedor_nombre || oc?.nombre_proveedor || oc?.proveedor
                    || ordenesFiltradas.find((o) => String(o.numero_oc) === String(selectedOC))?.proveedor_nombre
                    || oc?.rut_proveedor || '';
                  return `Detalle OC: ${selectedOC}${nombre ? ` - ${nombre}` : ''}`;
                })()}
              </h3>
              <Button variant="ghost" size="sm" onClick={closeDetalle}>✕</Button>
            </div>
            <div className="p-3 sm:p-4 overflow-auto flex-1">
              {loadingDetalle ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                </div>
              ) : detalleOC ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Columna izquierda: PDF preview */}
                  <div className="order-2 lg:order-1 border rounded-lg overflow-hidden bg-gray-50 flex flex-col" data-testid="detalle-pdf-preview">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 border-b">
                      <FileSearch className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-gray-600">Vista Previa PDF</span>
                      <div className="ml-auto">
                        <Button size="sm" variant="outline" onClick={() => handleDescargarPDF(selectedOC)} className="h-6 gap-1 text-[11px] text-blue-700 border-blue-300 hover:bg-blue-50">
                          <Download className="w-3 h-3" /> Abrir
                        </Button>
                      </div>
                    </div>
                    {detallePdfLoading ? (
                      <div className="flex items-center justify-center h-[340px] lg:h-[560px]">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                      </div>
                    ) : detallePdfUrl ? (
                      <iframe
                        src={detallePdfUrl}
                        className="w-full h-[340px] lg:h-[560px] border-0"
                        title="Vista previa OC PDF"
                        data-testid="detalle-pdf-iframe"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-[340px] lg:h-[560px] text-xs text-muted-foreground p-4 text-center">
                        No se pudo cargar la vista previa del PDF
                      </div>
                    )}
                  </div>

                  {/* Columna derecha: Detalle */}
                  <div className="order-1 lg:order-2 space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Proveedor:</span> <strong className="block truncate">{detalleOC.orden_compra?.proveedor_nombre || detalleOC.orden_compra?.nombre_proveedor || detalleOC.orden_compra?.proveedor || ordenesFiltradas.find((o) => String(o.numero_oc) === String(selectedOC))?.proveedor_nombre || detalleOC.orden_compra?.rut_proveedor || '-'}</strong></div>
                      <div><span className="text-muted-foreground">Fecha:</span> <strong>{fmtFecha(detalleOC.orden_compra?.fecha)}</strong></div>
                      <div><span className="text-muted-foreground">OT:</span> <strong>{detalleOC.orden_compra?.ot_numero}</strong></div>
                      <div><span className="text-muted-foreground">Estado:</span>
                        {detalleOC.orden_compra?.anulada ?
                          <span className="text-red-600 font-bold ml-1">ANULADA</span> :
                        detalleOC.orden_compra?.aprobada ?
                          <span className="text-green-600 font-medium ml-1">Aprobada</span> :
                          <span className="text-orange-500 font-medium ml-1">Pendiente VB</span>
                        }
                      </div>
                    </div>
                    {/* Banner OC anulada */}
                    {detalleOC.orden_compra?.anulada && (
                      <div className="rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2 text-xs flex items-start gap-2" data-testid="banner-oc-anulada">
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-bold text-red-800">OC Anulada</p>
                          {detalleOC.orden_compra?.anulada_motivo && (
                            <p className="text-red-700 mt-0.5">Motivo: {detalleOC.orden_compra.anulada_motivo}</p>
                          )}
                          {detalleOC.orden_compra?.anulada_at && (
                            <p className="text-red-600 mt-0.5 text-[10px]">Anulada el {fmtFecha(detalleOC.orden_compra.anulada_at)} por {detalleOC.orden_compra?.anulada_por_nombre || detalleOC.orden_compra?.anulada_por || '—'}</p>
                          )}
                          {canAnular && (
                            <Button size="sm" variant="outline" onClick={() => handleDesanular(selectedOC)}
                              className="mt-2 gap-1.5 border-green-300 text-green-700 hover:bg-green-50"
                              data-testid="modal-desanular-oc">
                              <RotateCcw className="w-3.5 h-3.5" /> Desanular OC
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Botón VB grande (prominente, fácil de tocar en móvil) */}
                    {!detalleOC.orden_compra?.anulada && !detalleOC.orden_compra?.aprobada && canVB && (
                      <Button size="lg" onClick={() => handleAprobar(selectedOC)} className="w-full gap-2 bg-green-600 hover:bg-green-700 h-12 text-base" data-testid="modal-aprobar-oc">
                        <ShieldCheck className="w-5 h-5" /> Dar VB a esta OC
                      </Button>
                    )}
                    {!detalleOC.orden_compra?.anulada && !detalleOC.orden_compra?.aprobada && canEdit && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { closeDetalle(); navigate(`/compras/editar-oc/${selectedOC}`); }}
                        className="w-full gap-2 text-amber-700 border-amber-300 hover:bg-amber-50"
                        data-testid="modal-editar-oc"
                      >
                        <Pencil className="w-4 h-4" /> Editar OC
                      </Button>
                    )}
                    <div className="grid grid-cols-3 gap-2 text-sm bg-gray-50 p-3 rounded">
                      <div><span className="text-muted-foreground text-xs">Neto:</span> <strong className="block text-green-600">${fmt(detalleOC.orden_compra?.neto)}</strong></div>
                      <div><span className="text-muted-foreground text-xs">IVA:</span> <strong className="block">${fmt(detalleOC.orden_compra?.iva)}</strong></div>
                      <div><span className="text-muted-foreground text-xs">Total:</span> <strong className="block">${fmt(detalleOC.orden_compra?.total)}</strong></div>
                    </div>
                    {/* Iter 313.j.12 — desglose extendido (retención, otros impuestos, neto exento)
                        cuando la OC no es afecta simple */}
                    {(detalleOC.orden_compra?.retencion_monto > 0 || (detalleOC.orden_compra?.otros_impuestos || []).length > 0 || detalleOC.orden_compra?.neto_exento > 0) && (
                      <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-0.5" data-testid="modal-desglose-impuestos">
                        {detalleOC.orden_compra?.tipo_oc && (
                          <div className="text-amber-800 font-semibold uppercase tracking-wide text-[10px] mb-1">Tipo OC: {detalleOC.orden_compra.tipo_oc}</div>
                        )}
                        {detalleOC.orden_compra?.retencion_monto > 0 && (
                          <div className="flex justify-between text-purple-700">
                            <span>Retención ({fmt(detalleOC.orden_compra.retencion_porcentaje)}%)</span>
                            <span className="font-mono">−${fmt(detalleOC.orden_compra.retencion_monto)}</span>
                          </div>
                        )}
                        {(detalleOC.orden_compra?.otros_impuestos || []).map((oi, i) => (
                          <div key={i} className="flex justify-between text-orange-700">
                            <span>{oi.nombre || `Otro impuesto ${i+1}`}</span>
                            <span className="font-mono">${fmt(oi.monto)}</span>
                          </div>
                        ))}
                        {detalleOC.orden_compra?.neto_exento > 0 && (
                          <div className="flex justify-between text-teal-700">
                            <span>Neto Exento</span>
                            <span className="font-mono">${fmt(detalleOC.orden_compra.neto_exento)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {detalleOC.orden_compra?.aprobada && (
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleDescargarPDF(selectedOC)} className="gap-1.5 text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="modal-pdf-oc">
                          <Download className="w-4 h-4" /> Descargar PDF
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { closeDetalle(); openEmailDialog(selectedOC); }} className="gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="modal-email-oc">
                          <Send className="w-4 h-4" /> Enviar a Proveedor
                        </Button>
                        {canDesbloquear && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={openDesbloquearDialog}
                            className="gap-1.5 text-orange-700 border-orange-300 hover:bg-orange-50"
                            data-testid="modal-desbloquear-oc"
                            title="Desbloquear OC aprobada para que el comprador pueda editarla (requiere motivo)"
                          >
                            <Unlock className="w-4 h-4" /> Desbloquear para editar
                          </Button>
                        )}
                        {canAnular && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={openAnularDialog}
                            className="gap-1.5 text-red-700 border-red-300 hover:bg-red-50"
                            data-testid="modal-anular-oc"
                            title="Anular esta OC (soft delete con motivo)"
                          >
                            <XCircle className="w-4 h-4" /> Anular OC
                          </Button>
                        )}
                      </div>
                    )}
                    {/* Botón anular para OCs no aprobadas */}
                    {!detalleOC.orden_compra?.anulada && !detalleOC.orden_compra?.aprobada && canAnular && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openAnularDialog}
                        className="w-full gap-1.5 text-red-700 border-red-300 hover:bg-red-50"
                        data-testid="modal-anular-oc-pendiente"
                      >
                        <XCircle className="w-4 h-4" /> Anular OC
                      </Button>
                    )}
                    {/* Banner OC desbloqueada */}
                    {detalleOC.orden_compra?.desbloqueada && !detalleOC.orden_compra?.aprobada && (
                      <div
                        className="rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-xs flex items-start gap-2"
                        data-testid="banner-oc-desbloqueada"
                      >
                        <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-orange-800">OC desbloqueada por Gerencia</p>
                          {detalleOC.orden_compra?.desbloqueada_motivo && (
                            <p className="text-orange-700 mt-0.5">Motivo: {detalleOC.orden_compra.desbloqueada_motivo}</p>
                          )}
                          <p className="text-orange-600 mt-0.5">El comprador puede editar y luego solicitar nueva aprobación de Gerencia.</p>
                        </div>
                      </div>
                    )}

                    {/* Historial de Desbloqueos (Carlos: línea de tiempo) */}
                    {Array.isArray(detalleOC.orden_compra?.desbloqueos) && detalleOC.orden_compra.desbloqueos.length > 0 && (
                      <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3" data-testid="historial-desbloqueos">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-orange-800 mb-2">
                          <Unlock className="w-3.5 h-3.5" />
                          Historial de Desbloqueos ({detalleOC.orden_compra.desbloqueos.length})
                        </div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {[...detalleOC.orden_compra.desbloqueos].reverse().map((d, i) => (
                            <div key={i} className="flex items-start gap-2 text-[11px] border-l-2 border-orange-400 pl-2 py-1 bg-white rounded-r">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono text-orange-700">
                                    {d.fecha ? new Date(d.fecha).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </span>
                                  <span className="text-gray-500">·</span>
                                  <span className="font-semibold text-gray-700">{d.desbloqueado_por_nombre || d.desbloqueado_por || d.usuario || '-'}</span>
                                </div>
                                {d.motivo && (
                                  <p className="text-gray-600 mt-0.5 italic">"{d.motivo}"</p>
                                )}
                                {d.estado_previo && (
                                  <p className="text-[10px] text-gray-500 mt-0.5">Estado previo: <code>{d.estado_previo}</code></p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold mb-2 text-sm">Líneas ({detalleOC.cantidad_lineas})</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-200">
                              <th className="border p-1 text-left">Item</th>
                              <th className="border p-1 text-left">Código</th>
                              <th className="border p-1 text-left">Descripción</th>
                              <th className="border p-1 text-center">Unidad</th>
                              <th className="border p-1 text-right">Cant.</th>
                              <th className="border p-1 text-right">P.Unit</th>
                              <th className="border p-1 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detalleOC.detalles?.map((d, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="border p-1 text-center">{d.item}</td>
                                <td className="border p-1 font-mono">{d.codigo}</td>
                                <td className="border p-1">{d.descripcion}</td>
                                <td className="border p-1 text-center">{d.unidad}</td>
                                <td className="border p-1 text-right">{fmt(d.cantidad)}</td>
                                <td className="border p-1 text-right">${fmt(d.precio_unitario)}</td>
                                <td className="border p-1 text-right font-medium">${fmt(d.total_linea)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground">No se encontró detalle</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Table (desktop/tablet >= md) */}
      <div className="hidden md:block border rounded-lg" style={{ overflowX: 'clip' }}>
      <table className="w-full text-xs border-collapse">
        <thead className="sticky top-14 lg:top-0 z-20">
          <tr className="bg-gray-800 text-white">
            <th className="border border-gray-600 p-1 text-center">Origen</th>
            <th className="border border-gray-600 p-1 text-left">N OC</th>
            <th className="border border-gray-600 p-1 text-left">Proveedor</th>
            <th className="border border-gray-600 p-1 text-left">Solicitante</th>
            <th className="border border-gray-600 p-1 text-center">OT</th>
            <th className="border border-gray-600 p-1 text-center">Fecha</th>
            <th className="border border-gray-600 p-1 text-left">Cond. Pago</th>
            <th className="border border-gray-600 p-1 text-right">Neto</th>
            <th className="border border-gray-600 p-1 text-right">Total</th>
            <th className="border border-gray-600 p-1 text-center">Estado</th>
            <th className="border border-gray-600 p-1 text-center" title="¿Requiere recepción de bodega?">📦 Recep.</th>
            <th className="border border-gray-600 p-1 text-center" title="Documento físico recibido del proveedor (factura/guía/boleta)">Doc. Recibido</th>
            <th className="border border-gray-600 p-1 text-center print:hidden">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {ordenesFiltradas.map((oc, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="border p-1 text-center">
                {oc.source === 'web' ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                    <Globe className="w-3 h-3" /> Web
                  </span>
                ) : String(oc.source || '').startsWith('rendicion_express') ? (
                  // Iter 313.j.11 (Renzo, feb-2026): OC originada en Rendición de Gastos.
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200" title={oc.source_rendicion_folio ? `Rendición ${oc.source_rendicion_folio}` : ''}>
                    <Wallet className="w-3 h-3" /> Rend. Gastos
                  </span>
                ) : oc.source === 'express' || String(oc.numero_oc || '').startsWith('W') ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                    <Zap className="w-3 h-3" /> Express
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700 border border-sky-200">
                    <Database className="w-3 h-3" /> Access
                  </span>
                )}
              </td>
              <td className="border p-1 font-mono font-medium">
                <button
                  type="button"
                  onClick={() => handleVerDetalle(oc.numero_oc)}
                  className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                  title={`Ver OC ${oc.numero_oc}`}
                  data-testid={`oc-num-link-${oc.numero_oc}`}
                >
                  <OCNumber oc={oc} size="sm" />
                </button>
              </td>
              <td className="border p-1 truncate max-w-[150px]">{oc.proveedor_nombre || oc.proveedor || oc.rut_proveedor || '-'}</td>
              <td className="border p-1 text-xs truncate max-w-[100px]">{oc.solicitante || '-'}</td>
              <td className="border p-1 text-center font-mono">{oc.ot_numero || '-'}</td>
              <td className="border p-1 text-center">{fmtFecha(oc.fecha)}</td>
              <td className="border p-1 text-xs truncate max-w-[100px]">{oc.condicion_pago_desc || oc.condicion_pago || '-'}</td>
              <td className="border p-1 text-right font-mono text-green-600">${fmt(oc.neto)}</td>
              <td className="border p-1 text-right font-mono" data-testid={`oc-total-${oc.numero_oc}`}>
                {oc.moneda && oc.moneda !== 'CLP' ? (
                  <>
                    <span className="text-[9px] font-semibold text-emerald-700 mr-1">{oc.moneda}</span>
                    ${fmt(oc.total)}
                    <button
                      className="text-[9px] text-blue-600 hover:text-blue-800 hover:underline ml-1"
                      title="Ver historial de TC de esta OC"
                      data-testid={`tc-historial-btn-${oc.numero_oc}`}
                      onClick={(e) => { e.stopPropagation(); setTcHistorialOC(oc.numero_oc); }}
                    >
                      📊
                    </button>
                    {oc.total_clp && (
                      <div className="text-[9px] text-gray-500" title={`TC ${oc.tipo_cambio} (${oc.tipo_cambio_fecha || ''}) · Referencial`}>
                        ≈ ${fmt(oc.total_clp)} CLP
                      </div>
                    )}
                  </>
                ) : (
                  <>${fmt(oc.total)}</>
                )}
              </td>
              <td className="border p-1 text-center">
                {oc.aprobada ? (
                  <span className="inline-flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-3 h-3" /> Aprobada
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-orange-500">
                    <Clock className="w-3 h-3" /> Pendiente
                  </span>
                )}
              </td>
              {/* Iter 236.m.16 (Carlos): Toggle "Requiere Recepción" + estado */}
              <td className="border p-1 text-center" data-testid={`recep-cell-${oc.numero_oc}`}>
                {(() => {
                  const req = !!oc.requiere_recepcion;
                  const tieneRec = !!oc.tiene_recepcion;
                  const cerrada = !!oc.recepcion_cerrada;
                  if (togglingOC === oc.numero_oc) {
                    return <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500 mx-auto" />;
                  }
                  if (!req) {
                    return canToggleRecepcion ? (
                      <button
                        onClick={() => toggleRequiereRecepcion(oc)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                        title="Marcar OC para que aparezca en el listado de Bodega"
                        data-testid={`btn-pedir-recepcion-${oc.numero_oc}`}
                      >
                        <Package className="w-3 h-3" /> Pedir
                      </button>
                    ) : (
                      <span className="text-muted-foreground text-[10px]">—</span>
                    );
                  }
                  // requiere_recepcion = true
                  if (tieneRec) {
                    return (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800"
                        title="Ya tiene recepción registrada"
                        data-testid={`badge-recep-ok-${oc.numero_oc}`}
                      >
                        <CheckCircle className="w-3 h-3" /> Recepcionada
                      </span>
                    );
                  }
                  if (cerrada) {
                    return canToggleRecepcion ? (
                      <button
                        onClick={() => toggleRequiereRecepcion(oc)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-700 border border-slate-200"
                        title="Recepción cerrada manualmente. Click para volver a solicitarla."
                        data-testid={`btn-reabrir-recepcion-${oc.numero_oc}`}
                      >
                        <Unlock className="w-3 h-3 rotate-90" /> Cerrada
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                        <Unlock className="w-3 h-3 rotate-90" /> Cerrada
                      </span>
                    );
                  }
                  // Pendiente
                  return (
                    <div className="inline-flex items-center gap-1">
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800"
                        title="Pendiente: aparece en listado de Bodega"
                        data-testid={`badge-recep-pendiente-${oc.numero_oc}`}
                      >
                        <Package className="w-3 h-3" /> En Bodega
                      </span>
                      {canToggleRecepcion && (
                        <button
                          onClick={() => {
                            setRecepExpressForm({ tipo: oc.tipo_documento === 'factura' ? 'FA' : 'GD', numDoc: oc.numero_documento || '', destino: 'bodega' });
                            setRecepExpressOC(oc);
                          }}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
                          title="Recepción exprés: recibir TODOS los ítems pendientes ahora"
                          data-testid={`btn-recep-express-${oc.numero_oc}`}
                        >
                          <Zap className="w-3 h-3" /> Exprés
                        </button>
                      )}
                      {canToggleRecepcion && (
                        <button
                          onClick={() => toggleRequiereRecepcion(oc)}
                          className="text-[10px] text-gray-400 hover:text-red-600 transition-colors w-4 h-4 inline-flex items-center justify-center hover:bg-red-50 rounded-full"
                          title="Quitar de Bodega (no requiere recepción)"
                          data-testid={`btn-quitar-recepcion-${oc.numero_oc}`}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })()}
              </td>
              <td className="border p-1 text-center" data-testid={`doc-recibido-${oc.numero_oc}`}>
                {oc.numero_documento ? (
                  <button
                    type="button"
                    onClick={() => openDoc({ tipo: 'dte', folio: oc.numero_documento, rut: oc.rut_proveedor })}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold cursor-pointer hover:brightness-95 hover:underline ${
                      oc.tipo_documento === 'factura' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : oc.tipo_documento === 'guia' ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                      : oc.tipo_documento === 'boleta' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                      : 'bg-slate-100 text-slate-700'
                    }`}
                    title={`Ver ${(oc.tipo_documento || 'doc').toUpperCase()}: ${oc.numero_documento}`}
                    data-testid={`doc-recibido-link-${oc.numero_oc}`}
                  >
                    {oc.tipo_documento === 'factura' ? 'F' : oc.tipo_documento === 'guia' ? 'G' : oc.tipo_documento === 'boleta' ? 'B' : '•'}
                    <span>{oc.numero_documento}</span>
                  </button>
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </td>
              <td className="border p-1 text-center print:hidden">
                <div className="flex items-center justify-center gap-1">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleVerDetalle(oc.numero_oc)} data-testid={`ver-oc-${oc.numero_oc}`}>
                    <Eye className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleDescargarPDF(oc.numero_oc)} title="Ver PDF" data-testid={`pdf-oc-${oc.numero_oc}`}>
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  {!oc.aprobada && canEdit && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => navigate(`/compras/editar-oc/${oc.numero_oc}`)} title="Editar OC" data-testid={`edit-oc-${oc.numero_oc}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {oc.aprobada && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => openEmailDialog(oc.numero_oc)} title="Enviar por correo" data-testid={`email-oc-${oc.numero_oc}`}>
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {oc.aprobada && canDesbloquear && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      onClick={() => { setSelectedOC(oc.numero_oc); openDesbloquearDialog(); }}
                      title="Desbloquear OC aprobada para permitir edición"
                      data-testid={`desbloquear-oc-${oc.numero_oc}`}
                    >
                      <Unlock className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {!oc.aprobada && canVB && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => handleAprobar(oc.numero_oc)} title="Dar VB" data-testid={`vb-oc-${oc.numero_oc}`}>
                      <ShieldCheck className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {/* Iter 236.y (Renzo, feb-2026) — Repetir OC: R en círculo verde */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 hover:text-emerald-800 font-bold text-[11px] leading-none"
                    onClick={() => handleRepetirOC(oc.numero_oc)}
                    title="Repetir OC (clona los datos en una nueva OC)"
                    data-testid={`repetir-oc-${oc.numero_oc}`}
                  >
                    R
                  </Button>
                  {/* Iter 236.y (Renzo, feb-2026) — Registrar Egreso Alegra: admin/gerencia + condición contado/TC/cheque + aprobada + sin egreso previo */}
                  {/* Iter 415 (Renzo, jun-2026) — Las OC/OC Express originadas en Rendición de Gastos NO admiten pago rápido (el egreso se liquida por la Rendición). */}
                  {canAnular && oc.aprobada && !oc.anulada && !oc.egreso_alegra_id && !String(oc.source || '').startsWith('rendicion_express') && (() => {
                    const cp = (oc.condicion_pago || '').toLowerCase();
                    return cp === 'cp-contado' || cp === 'cp-tarjeta-credito' ||
                      cp.startsWith('cp-cheque-') ||
                      cp.startsWith('contado') || cp.includes('tarjeta') || cp.startsWith('cheque');
                  })() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-green-700 hover:text-green-800 hover:bg-green-50 font-bold"
                      onClick={() => openEgresoAlegra(oc)}
                      title={`Registrar Egreso en Alegra (Cta. Avances y anticipos entregados) · ${oc.condicion_pago}`}
                      data-testid={`egreso-alegra-oc-${oc.numero_oc}`}
                    >
                      <DollarSign className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {oc.egreso_alegra_id && (
                    <span
                      className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-600 text-white"
                      title={`Egreso ya registrado en Alegra (id=${oc.egreso_alegra_id})`}
                      data-testid={`egreso-alegra-registrado-${oc.numero_oc}`}
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Card view (mobile < md) */}
      <div className="md:hidden space-y-2" data-testid="oc-mobile-cards">
        {ordenesFiltradas.map((oc, idx) => {
          const aprobada = !!oc.aprobada;
          return (
            <div key={idx} className={`border rounded-lg p-3 shadow-sm ${aprobada ? 'bg-white' : 'bg-orange-50 border-orange-200'}`} data-testid={`oc-card-${oc.numero_oc}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {oc.source === 'web' ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                      <Globe className="w-3 h-3" /> Web
                    </span>
                  ) : String(oc.source || '').startsWith('rendicion_express') ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200" title={oc.source_rendicion_folio ? `Rendición ${oc.source_rendicion_folio}` : ''}>
                      <Wallet className="w-3 h-3" /> Rend. Gastos
                    </span>
                  ) : oc.source === 'express' || String(oc.numero_oc || '').startsWith('W') ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                      <Zap className="w-3 h-3" /> Express
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700 border border-sky-200">
                      <Database className="w-3 h-3" /> Access
                    </span>
                  )}
                  <span className="font-mono font-bold text-sm"><OCNumber oc={oc} size="sm" /></span>
                </div>
                {aprobada ? (
                  <span className="inline-flex items-center gap-1 text-green-600 text-xs whitespace-nowrap">
                    <CheckCircle className="w-3 h-3" /> Aprobada
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-orange-600 text-xs font-semibold whitespace-nowrap">
                    <Clock className="w-3 h-3" /> Pendiente
                  </span>
                )}
              </div>

              <div className="text-sm font-medium truncate mb-1" title={oc.proveedor_nombre || oc.proveedor}>
                {oc.proveedor_nombre || oc.proveedor || oc.rut_proveedor || '-'}
              </div>

              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs text-muted-foreground mb-2">
                <div>OT: <span className="font-mono text-foreground">{oc.ot_numero || '-'}</span></div>
                <div>Fecha: <span className="text-foreground">{fmtFecha(oc.fecha)}</span></div>
                {oc.solicitante && <div className="col-span-2 truncate">Solic: <span className="text-foreground">{oc.solicitante}</span></div>}
              </div>

              {/* Iter 236.m.16 (Carlos): Badge Recepción en mobile card */}
              {(() => {
                const req = !!oc.requiere_recepcion;
                const tieneRec = !!oc.tiene_recepcion;
                const cerrada = !!oc.recepcion_cerrada;
                if (req && !tieneRec && !cerrada) {
                  return (
                    <div className="flex items-center justify-between border-t pt-2 mb-1.5">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800"
                        data-testid={`badge-recep-card-${oc.numero_oc}`}
                      >
                        <Package className="w-3 h-3" /> Pendiente Bodega
                      </span>
                      {canToggleRecepcion && (
                        <button
                          onClick={() => toggleRequiereRecepcion(oc)}
                          className="text-[10px] text-gray-500 hover:text-red-600"
                          data-testid={`btn-quitar-recepcion-card-${oc.numero_oc}`}
                          disabled={togglingOC === oc.numero_oc}
                        >
                          {togglingOC === oc.numero_oc ? '…' : 'no requerir'}
                        </button>
                      )}
                    </div>
                  );
                }
                if (req && tieneRec) {
                  return (
                    <div className="border-t pt-2 mb-1.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                        <CheckCircle className="w-3 h-3" /> Recepcionada
                      </span>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="flex items-center justify-between border-t pt-2 mb-2">
                <div className="text-xs text-muted-foreground">Total</div>
                <div className="font-mono font-bold text-base">${fmt(oc.total)}</div>
              </div>

              {/* Botón VB grande (siempre visible si está pendiente) */}
              {!aprobada && canVB && (
                <Button
                  onClick={() => handleAprobar(oc.numero_oc)}
                  className="w-full h-11 gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold mb-2"
                  data-testid={`vb-card-${oc.numero_oc}`}
                >
                  <ShieldCheck className="w-5 h-5" /> Dar VB
                </Button>
              )}
              {!aprobada && canEdit && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/compras/editar-oc/${oc.numero_oc}`)}
                  className="w-full h-10 gap-2 text-amber-700 border-amber-300 hover:bg-amber-50 mb-2"
                  data-testid={`edit-card-${oc.numero_oc}`}
                >
                  <Pencil className="w-4 h-4" /> Editar OC
                </Button>
              )}

              {/* Acciones secundarias */}
              <div className="grid grid-cols-3 gap-1.5">
                <Button variant="outline" size="sm" className="h-9 gap-1 text-xs" onClick={() => handleVerDetalle(oc.numero_oc)} data-testid={`ver-card-${oc.numero_oc}`}>
                  <Eye className="w-3.5 h-3.5" /> Ver
                </Button>
                <Button variant="outline" size="sm" className="h-9 gap-1 text-xs text-blue-700 border-blue-300" onClick={() => handleDescargarPDF(oc.numero_oc)} data-testid={`pdf-card-${oc.numero_oc}`}>
                  <Download className="w-3.5 h-3.5" /> PDF
                </Button>
                {aprobada ? (
                  <Button variant="outline" size="sm" className="h-9 gap-1 text-xs text-emerald-700 border-emerald-300" onClick={() => openEmailDialog(oc.numero_oc)} data-testid={`email-card-${oc.numero_oc}`}>
                    <Send className="w-3.5 h-3.5" /> Email
                  </Button>
                ) : (
                  <div />
                )}
              </div>

              {/* Desbloquear para OCs aprobadas Web (solo admin/gerencia) */}
              {aprobada && canDesbloquear && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-9 gap-1.5 text-xs text-orange-700 border-orange-300 hover:bg-orange-50"
                  onClick={() => { setSelectedOC(oc.numero_oc); openDesbloquearDialog(); }}
                  data-testid={`desbloquear-card-${oc.numero_oc}`}
                >
                  <Unlock className="w-4 h-4" /> Desbloquear para editar
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {data.ordenes_compra.length === 0 && (
        <p className="text-center py-8 text-muted-foreground">No hay ordenes de compra</p>
      )}

      {/* Paginación */}
      {data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 py-3 border-t mt-2" data-testid="pagination-controls">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>
              Mostrando <strong>{(page - 1) * pageSize + 1}-{Math.min(page * pageSize, data.total)}</strong> de <strong>{data.total.toLocaleString('es-CL')}</strong> OCs
            </span>
            <label className="flex items-center gap-1.5 text-xs">
              Por página:
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
                className="border rounded px-2 py-1 text-xs bg-white"
                data-testid="select-page-size"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1 || loading}
              data-testid="btn-page-first"
            >« Primera</Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="btn-page-prev"
            >‹ Anterior</Button>
            <span className="text-sm font-medium px-2" data-testid="page-indicator">
              Página {page} de {Math.max(1, Math.ceil(data.total / pageSize))}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page * pageSize >= data.total || loading}
              data-testid="btn-page-next"
            >Siguiente ›</Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, Math.ceil(data.total / pageSize)))}
              disabled={page * pageSize >= data.total || loading}
              data-testid="btn-page-last"
            >Última »</Button>
          </div>
        </div>
      )}

      {/* Email Dialog with PDF Preview */}
      <Dialog open={emailOpen} onOpenChange={closeEmailDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" /> Enviar OC {emailOC} por Correo
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {/* Left: PDF Preview */}
            <div className="border rounded-lg overflow-hidden bg-gray-50" data-testid="pdf-preview-container">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border-b">
                <FileSearch className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-gray-600">Vista Previa PDF</span>
              </div>
              {pdfLoading ? (
                <div className="flex items-center justify-center h-[400px]">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : pdfPreviewUrl ? (
                <iframe
                  src={pdfPreviewUrl}
                  className="w-full h-[400px] border-0"
                  title="Vista previa OC PDF"
                  data-testid="pdf-preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-[400px] text-sm text-muted-foreground">
                  No se pudo cargar la vista previa
                </div>
              )}
            </div>

            {/* Right: Email Form */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center gap-1.5">
                  <ShoppingCart className="w-3.5 h-3.5 text-emerald-600" /> Correo de Compras del Proveedor
                </label>
                <Input value={emailDest} onChange={(e) => setEmailDest(e.target.value)}
                  placeholder="compras@proveedor.cl" className="text-sm" data-testid="email-dest" />
                {emailDest && emailMaestro && emailDest.trim().toLowerCase() !== emailMaestro.trim().toLowerCase() && (
                  <label className="flex items-start gap-2 mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 cursor-pointer" data-testid="email-actualizar-maestro-wrap">
                    <input type="checkbox" checked={actualizarMaestro} onChange={(e) => setActualizarMaestro(e.target.checked)}
                      className="mt-0.5" data-testid="email-actualizar-maestro" />
                    <span className="text-xs text-amber-800">
                      El correo difiere del guardado (<b>{emailMaestro}</b>). Marca esta casilla para <b>actualizar el correo de compras en el maestro del proveedor</b>; si no, solo se usa para este envío.
                    </span>
                  </label>
                )}
                {emailDest && !emailMaestro && (
                  <p className="text-[11px] text-emerald-700 mt-1" data-testid="email-sin-maestro-note">Este proveedor no tiene correo de compras: se guardará automáticamente en su ficha.</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Mensaje adicional (opcional)</label>
                <textarea value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)}
                  placeholder="Favor despachar a la brevedad..."
                  className="w-full h-24 px-3 py-2 rounded-md border border-input text-sm resize-none" data-testid="email-msg" />
              </div>
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
                <p className="text-xs text-blue-700">Se adjuntara el PDF mostrado a la izquierda. El correo se enviara desde <strong>compras@chirinosteel.com</strong>.</p>
              </div>

              {/* Historial de envíos de esta OC */}
              <div data-testid="email-historial">
                <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Envíos anteriores{emailHistorial.length ? ` (${emailHistorial.length})` : ''}
                </p>
                {emailHistorial.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Esta OC aún no se ha enviado por correo.</p>
                ) : (
                  <div className="max-h-28 overflow-y-auto border rounded-md divide-y">
                    {emailHistorial.map((e, i) => {
                      const ok = e.resultado === 'enviado' || e.resultado === 'SIMULADO';
                      return (
                        <div key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 text-[11px]" data-testid={`email-hist-${i}`}>
                          <div className="min-w-0">
                            <div className="truncate font-medium">{e.email_destino}</div>
                            <div className="text-muted-foreground">
                              {e.fecha_envio ? new Date(e.fecha_envio).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                              {e.enviado_por ? ` · ${e.enviado_por}` : ''}
                            </div>
                          </div>
                          <span className={`shrink-0 px-1.5 py-0.5 rounded-full font-semibold ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {e.resultado === 'SIMULADO' ? 'Simulado' : ok ? 'Enviado' : 'Error'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => closeEmailDialog(false)} className="flex-1">Cancelar</Button>
                <Button onClick={handleEnviarEmail} disabled={emailSending} className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="email-enviar">
                  {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Enviar
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Desbloquear OC aprobada */}
      {/* Iter 338 (Renzo): Recepción Exprés — recibe todos los ítems pendientes */}
      <Dialog open={!!recepExpressOC} onOpenChange={(v) => { if (!v) setRecepExpressOC(null); }}>
        <DialogContent className="max-w-md" data-testid="recep-express-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <Zap className="w-4 h-4" /> Recepción Exprés — OC {recepExpressOC?.numero_oc}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground text-xs">
              Se recepcionarán <b>todos los ítems pendientes</b> de la OC de {recepExpressOC?.proveedor_nombre || recepExpressOC?.nombre_proveedor || ''} con las cantidades completas. Quedará registrada como recepción normal de bodega.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium mb-1">Tipo documento</label>
                <select
                  value={recepExpressForm.tipo}
                  onChange={(e) => setRecepExpressForm(f => ({ ...f, tipo: e.target.value }))}
                  className="w-full h-9 px-2 rounded-md border border-input text-sm"
                  data-testid="recep-express-tipo"
                >
                  <option value="GD">Guía de Despacho</option>
                  <option value="FA">Factura</option>
                  <option value="BO">Boleta</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">N° documento *</label>
                <Input
                  value={recepExpressForm.numDoc}
                  onChange={(e) => setRecepExpressForm(f => ({ ...f, numDoc: e.target.value }))}
                  placeholder="Folio guía/factura"
                  className="h-9"
                  data-testid="recep-express-numdoc"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Destino</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRecepExpressForm(f => ({ ...f, destino: 'bodega' }))}
                  className={`flex-1 text-xs px-2 py-1.5 rounded-md border transition-colors ${recepExpressForm.destino === 'bodega' ? 'bg-teal-600 text-white border-teal-700' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="recep-express-destino-bodega"
                >
                  Bodega (suma stock)
                </button>
                <button
                  type="button"
                  onClick={() => setRecepExpressForm(f => ({ ...f, destino: 'directo_ot' }))}
                  className={`flex-1 text-xs px-2 py-1.5 rounded-md border transition-colors ${recepExpressForm.destino === 'directo_ot' ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="recep-express-destino-ot"
                >
                  Directo a OT (no suma stock)
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecepExpressOC(null)} disabled={recepExpressLoading}>Cancelar</Button>
            <Button
              onClick={handleRecepcionExpress}
              disabled={recepExpressLoading || !recepExpressForm.numDoc.trim()}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="recep-express-confirmar"
            >
              {recepExpressLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
              Recepcionar Todo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={desbloquearOpen} onOpenChange={setDesbloquearOpen}>
        <DialogContent className="max-w-md" data-testid="desbloquear-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Unlock className="w-5 h-5" /> Desbloquear OC #{selectedOC}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800">
                Esto quitará el VB de Gerencia y el VB de Adquisiciones. La OC volverá a estado
                <strong> pendiente de aprobación</strong> y el comprador podrá editarla. Después
                deberá pasar por todo el flujo de VB nuevamente.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" htmlFor="desbloquear-motivo-input">
                Motivo del desbloqueo <span className="text-red-500">*</span>
              </label>
              <textarea
                id="desbloquear-motivo-input"
                value={desbloquearMotivo}
                onChange={(e) => setDesbloquearMotivo(e.target.value)}
                placeholder="Ej: El proveedor subió los precios y hay que ajustar la OC."
                rows={3}
                className="w-full px-3 py-2 rounded-md border border-input text-sm resize-none"
                data-testid="desbloquear-motivo"
              />
              <p className="text-[11px] text-gray-500 mt-1">Mínimo 3 caracteres. Queda registrado en el historial de la OC.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDesbloquearOpen(false)} disabled={desbloquearLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleDesbloquear}
              disabled={desbloquearLoading || desbloquearMotivo.trim().length < 3}
              className="bg-orange-600 hover:bg-orange-700 gap-1.5"
              data-testid="desbloquear-confirmar"
            >
              {desbloquearLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
              Desbloquear OC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog: Anular OC */}
      <Dialog open={anularOpen} onOpenChange={setAnularOpen}>
        <DialogContent className="max-w-md" data-testid="anular-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" /> Anular OC #{selectedOC}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800">
                La OC quedará marcada como <strong>anulada</strong> y se excluirá de dashboards
                y listados. <strong>No se elimina físicamente</strong>: queda en histórico para
                auditoría y puede revertirse con "Desanular".
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" htmlFor="anular-motivo-input">
                Motivo de la anulación <span className="text-red-500">*</span>
              </label>
              <textarea
                id="anular-motivo-input"
                value={anularMotivo}
                onChange={(e) => setAnularMotivo(e.target.value)}
                placeholder="Ej: OC duplicada / proveedor canceló / error en datos."
                rows={3}
                className="w-full px-3 py-2 rounded-md border border-input text-sm resize-none"
                data-testid="anular-motivo"
              />
              <p className="text-[11px] text-gray-500 mt-1">Mínimo 5 caracteres. Queda registrado para auditoría.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAnularOpen(false)} disabled={anularLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleAnular}
              disabled={anularLoading || anularMotivo.trim().length < 5}
              className="bg-red-600 hover:bg-red-700 gap-1.5"
              data-testid="anular-confirmar"
            >
              {anularLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Anular OC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 236.y (Renzo, feb-2026) — Dialog: Confirmar Egreso Alegra */}
      <Dialog open={egresoDialog.open} onOpenChange={(o) => !egresoSaving && setEgresoDialog({ open: o, oc: o ? egresoDialog.oc : null })}>
        <DialogContent className="max-w-md" data-testid="egreso-alegra-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <DollarSign className="w-5 h-5" /> Registrar Egreso en Alegra
            </DialogTitle>
          </DialogHeader>
          {egresoLoading && !egresoPreview ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Validando OC y calculando cuenta...
            </div>
          ) : egresoPreview ? (
            <div className="space-y-2 text-sm">
              <p>
                Vas a registrar un <strong>Egreso</strong> en Alegra por la OC{' '}
                <strong className="font-mono">#{egresoPreview.numero_oc}</strong>.
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs space-y-1">
                <div><strong>Proveedor:</strong> {egresoPreview.provider_nombre}</div>
                <div><strong>Condición de pago:</strong> {egresoPreview.condicion_pago_desc}</div>
                <div><strong>Medio de pago:</strong> {egresoPreview.medio_pago_nombre || '(no aplica)'}</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs space-y-1">
                <div><strong>Cuenta contable:</strong> {egresoPreview.cuenta_alegra_nombre} (id {egresoPreview.cuenta_alegra_id})</div>
                <div><strong>Cuenta bancaria (banco):</strong> {egresoPreview.bank_account_name} (id {egresoPreview.bank_account_id})</div>
                <div><strong>Fecha egreso:</strong> {egresoPreview.fecha_egreso}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm font-bold text-amber-900 flex items-center justify-between">
                <span>Monto (Total con IVA)</span>
                <span data-testid="egreso-alegra-monto">${Math.round(egresoPreview.total).toLocaleString('es-CL')}</span>
              </div>
              <div className="text-[10px] text-gray-500 pt-1">
                Se creará como <em>Egreso</em> categorizado (POST /payments type=out). Idempotente: una vez creado se muestra ✅ y no permite duplicar.
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500 py-6 text-center">Sin datos de preview.</div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEgresoDialog({ open: false, oc: null })} disabled={egresoSaving} data-testid="egreso-alegra-cancelar">
              Cancelar
            </Button>
            <Button
              onClick={confirmEgresoAlegra}
              disabled={egresoSaving || !egresoPreview}
              className="bg-green-600 hover:bg-green-700 gap-1.5"
              data-testid="egreso-alegra-confirmar"
            >
              {egresoSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
              Registrar Egreso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 313.h — Modal Historial de TC */}
      <Dialog open={!!tcHistorialOC} onOpenChange={(o) => !o && setTcHistorialOC(null)}>
        <DialogContent className="max-w-3xl" data-testid="tc-historial-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📊 Historial de Tipo de Cambio
              {tcHistorialData?.numero_oc && (
                <span className="text-sm font-normal text-gray-500">OC #{tcHistorialData.numero_oc}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {tcHistorialLoading ? (
            <div className="py-8 text-center text-gray-500">Cargando…</div>
          ) : !tcHistorialData ? (
            <div className="py-8 text-center text-gray-400">Sin datos</div>
          ) : tcHistorialData.moneda === 'CLP' ? (
            <div className="py-6 text-center text-gray-500 text-sm">
              Esta OC está en CLP — no hay historial de TC.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                <div><strong>Proveedor:</strong> {tcHistorialData.nombre_proveedor || '-'}</div>
                <div><strong>Moneda:</strong> {tcHistorialData.moneda}</div>
                <div><strong>TC actual:</strong> ${(tcHistorialData.tc_actual || 0).toLocaleString('es-CL')} <span className="text-gray-500 text-xs">({tcHistorialData.tc_fecha_actual || '-'} · {tcHistorialData.tc_fuente_actual || '-'})</span></div>
                <div><strong>Total:</strong> {tcHistorialData.moneda} ${fmt(tcHistorialData.total)} ≈ ${fmt(tcHistorialData.total_clp)} CLP</div>
              </div>

              {tcHistorialData.diff_max && tcHistorialData.diff_max.abs > 0 && (
                <div className={`border rounded p-3 text-sm ${Math.abs(tcHistorialData.diff_max.pct) > 5 ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                  <div className="font-semibold flex items-center gap-1">
                    ⚠️ Diferencia máxima detectada: <span className="font-mono">${Math.round(tcHistorialData.diff_max.abs).toLocaleString('es-CL')}</span> ({tcHistorialData.diff_max.pct.toFixed(2)}%)
                  </div>
                  <div className="text-xs mt-1">
                    TC OC: ${tcHistorialData.diff_max.tc_oc.toLocaleString('es-CL')} vs TC evento ({tcHistorialData.diff_max.evento}): ${tcHistorialData.diff_max.tc_evento.toLocaleString('es-CL')} el {tcHistorialData.diff_max.fecha_evento}
                  </div>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Evento</th>
                      <th className="px-3 py-2 text-center">TC</th>
                      <th className="px-3 py-2 text-center">Fecha TC</th>
                      <th className="px-3 py-2 text-left">Fuente</th>
                      <th className="px-3 py-2 text-left">Extra</th>
                      <th className="px-3 py-2 text-center">Registrado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tcHistorialData.historial || []).map((e, i) => (
                      <tr key={i} className="border-t hover:bg-gray-50" data-testid={`tc-historial-row-${i}`}>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                            e.evento === 'creacion' ? 'bg-blue-100 text-blue-700' :
                            e.evento === 'dte_recibido' ? 'bg-emerald-100 text-emerald-700' :
                            e.evento === 'edicion' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{e.evento}</span>
                        </td>
                        <td className="px-3 py-2 text-center font-mono">${(e.tc || 0).toLocaleString('es-CL')}</td>
                        <td className="px-3 py-2 text-center text-gray-600">{e.fecha_tc || '-'}</td>
                        <td className="px-3 py-2 text-gray-500">{e.fuente || '-'}</td>
                        <td className="px-3 py-2 text-gray-500 text-[10px]">
                          {e.extra?.folio_dte && <span title={`DTE ${e.extra.tipo_dte || ''}`}>DTE {e.extra.folio_dte}</span>}
                          {e.extra?.diff_pct !== undefined && (
                            <span className={`ml-1 ${Math.abs(e.extra.diff_pct) > 5 ? 'text-red-600' : 'text-amber-600'}`}>
                              ({e.extra.diff_pct >= 0 ? '+' : ''}{e.extra.diff_pct.toFixed(2)}%)
                            </span>
                          )}
                          {e.extra?.contexto && <span className="text-gray-400">{e.extra.contexto}</span>}
                        </td>
                        <td className="px-3 py-2 text-center text-[10px] text-gray-500" title={`por ${e.usuario || 'sistema'}`}>
                          {(e.ts || '').slice(0, 16).replace('T', ' ')}
                        </td>
                      </tr>
                    ))}
                    {(!tcHistorialData.historial || tcHistorialData.historial.length === 0) && (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Sin eventos registrados</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
